import api, { AppDocument, AppDocStatus } from 'api';
import Skeleton from '@mui/material/Skeleton';
import { Doc } from 'data/constants/Doc';
import Modal from '@mui/material/Modal';
import { Visibility } from '@mui/icons-material';
import { DocStatus } from 'data/constants/DocStatus';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { RootState, AppDispatch } from 'redux/store';
import { useDispatch, useSelector } from 'react-redux';
import { loadDocs, deleteDocs } from 'redux/application/documents/documents-middleware';
import { patchDocProperty } from 'redux/application/document/document.middleware';
import { reset as resetDocStore } from 'redux/application/document/document.slice';
import { reset as resetDocsListStore } from 'redux/application/documents/documents-slice';
import { updateDoc } from 'redux/application/document/document.middleware';
import { LTHeader, LTItem, LTRow, LTPlaceholder, LTMobileItem } from 'components/common/ListTable';
import { CustomLTRow } from '../../../../customLTRow/customLTRow';
import FilePicker from 'components/common/FilePicker/FilePicker';
import AlertMessage, { AlertObj } from 'components/common/alert';
import IconBtn from 'components/common/IconBtn';
import { SvgIcon } from '@mui/material';
import { BiTrash } from 'react-icons/bi';
import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import { ReactComponent as UploadIcon } from 'assets/icons/upload.svg';
import { ReactComponent as CrossSimpleIcon } from 'assets/icons/cross-simple.svg';
import { ReactComponent as BellIcon } from 'assets/icons/B2B/bell.svg';
import DocumentPreview from 'components/common/DocumentPreview/DocumentPreview';
import UrlDocumentPreview from 'components/v2/common/documentPreview';
import { useWindowSize } from 'hooks/useWindowSize';
import * as S from '../../../../Table.styled';
import { updateDocInList } from 'redux/application/documents/documents-slice';
import DeleteDocumentConfirmationModal from 'views/Dashboards-B2B/components/DeleteDocumentConfirmationModal';
import CreateTicketModal from 'views/Dashboards-B2B/components/BACKOFFICEFLOW/CreateTicketModal';
import DocumentDescriptionModal from './components/DocumentDescriptionModal';
import { useTranslation } from 'react-i18next';
import { Colors } from 'styles/variables/colors';
import DocumentActionsPopover from './components/DocumentActionsPopover';
import SelectDocStatusDropdown from './components/SelectDocStatusDropdown';
import { StatusColor } from 'data/constants/caseStatus';
// import SelectCategoryDropdown from './components/SelectCategoryDropdown';
import DocumentTableSelector from 'views/Dashboards-B2B/components/CLIENTFLOW/documents/DocumentTableSelector';
import { TableSelection } from 'views/Dashboards-B2B/components/CLIENTFLOW/documents/DocumentTableSelector';
import { ClientApplicationStatus } from 'api/models/ClientApplicationStatus';
import { getDaysSince } from 'utils/dateUtils';
import { ReactComponent as ExclMark } from 'assets/icons/B2B/round_exclamation_mark.svg';

type CombinedDocType = Doc | AppDocument | null;
type ClientDocument = AppDocument & {
    sample_uri?: string | null;
    template_uri?: string | null;
};
type PreviewFile = {
    fileUrl: string;
    fileName: string;
    title: string;
} | null;

export default function ClientDocuments() {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const { id: candidateID } = useParams();
    const { entity: activeApp } = useSelector((state: RootState) => state.application);
    const { list: docsList, isLoading: isDocsLoading } = useSelector(
        (state: RootState) => state.documentsApp
    );
    const [isShowingAlert, setShowingAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState<AlertObj>({
        type: 'info',
        value: '',
    });
    const [isDocViewerOpen, setDocViewerOpen] = useState<boolean>(false);
    const [docForPDFViewer, setDocForPDFViewer] = useState<AppDocument | Doc | null>(null);
    const skeletonArray = docsList.length > 0 ? docsList : [1, 2, 3];
    const [width, height] = useWindowSize();
    const [mobile, setMobile] = useState(false);
    const [deleteDocConfirmationModalOpen, setDeleteDocConfirmationModalOpenClose] =
        useState<boolean>(false);
    const [documentToDelete, setDocumentToDelete] = useState<AppDocument | null>(null);
    const [documentTableSelection, setDocumentTableSelection] = useState<TableSelection>(
        TableSelection.GENERAL
    );
    const [generalDocs, setGeneralDocs] = useState<ClientDocument[]>([]);
    const [postItaDocs, setPostItaDocs] = useState<ClientDocument[]>([]);
    const [postSubmissionDocs, setPostSubmissionDocs] = useState<ClientDocument[]>([]);
    const [docsForTable, setDocsForTable] = useState<ClientDocument[]>([]);
    const [previewFile, setPreviewFile] = useState<PreviewFile>(null);
    const [ticketDocument, setTicketDocument] = useState<AppDocument | null>(null);
    const [descriptionDocument, setDescriptionDocument] = useState<AppDocument | null>(null);

    useEffect(() => {
        if (width >= 769) {
            setMobile(false);
        }
        if (width < 769) {
            setMobile(true);
        }
    }, [width, height]);

    useEffect(() => {
        if (activeApp) {
            dispatch(loadDocs(activeApp.id.toString()));
        }
    }, [activeApp, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(resetDocStore());
            dispatch(resetDocsListStore());
        };
    }, [candidateID, dispatch]);

    useEffect(() => {
        if (!activeApp?.client_status) return;

        let preferred: TableSelection = TableSelection.GENERAL;

        switch (activeApp.client_status) {
            case ClientApplicationStatus.READY_FOR_SUBMISSION:
            case ClientApplicationStatus.AWAITING_LOA:
                preferred = TableSelection.POST_ITA;
                break;

            case ClientApplicationStatus.SUBMITTED:
            case ClientApplicationStatus.VISA_RESULTS:
                preferred = TableSelection.POST_SUBMISSION;
                break;

            default:
                preferred = TableSelection.GENERAL;
        }

        const mapping: Record<TableSelection, ClientDocument[]> = {
            [TableSelection.GENERAL]: generalDocs,
            [TableSelection.POST_ITA]: postItaDocs,
            [TableSelection.POST_SUBMISSION]: postSubmissionDocs,
        };

        const docsForPreferred = mapping[preferred];

        if (docsForPreferred.length === 0) {
            setDocumentTableSelection(TableSelection.GENERAL);
        } else {
            setDocumentTableSelection(preferred);
        }
    }, [activeApp, generalDocs, postItaDocs, postSubmissionDocs]);

    useEffect(() => {
        if (docsList.length > 0 && activeApp) {
            const generalDocuments: ClientDocument[] = [];
            const postItaDocuments: ClientDocument[] = [];
            const postSubmissionDocuments: ClientDocument[] = [];

            docsList.forEach((doc) => {
                if (doc.explanation?.includes(t('postita', { lng: language }))) {
                    postItaDocuments.push(doc);
                } else if (doc.explanation?.includes(t('postsubmission', { lng: language }))) {
                    postSubmissionDocuments.push(doc);
                } else {
                    generalDocuments.push(doc);
                }
            });
            setGeneralDocs(generalDocuments);
            setPostItaDocs(postItaDocuments);
            setPostSubmissionDocs(postSubmissionDocuments);
        }
    }, [docsList, activeApp, language, t]);

    useEffect(() => {
        switch (documentTableSelection) {
            case TableSelection.GENERAL:
                setDocsForTable(generalDocs);
                break;
            case TableSelection.POST_ITA:
                setDocsForTable(postItaDocs);
                break;
            case TableSelection.POST_SUBMISSION:
                setDocsForTable(postSubmissionDocs);
                break;
            default:
                setDocsForTable(generalDocs);
        }
    }, [documentTableSelection, generalDocs, postItaDocs, postSubmissionDocs]);

    const displayName = (doc: CombinedDocType, candidateName: string) => {
        if (doc) {
            if ('name' in doc) {
                return candidateName + ' - ' + doc.name;
            } else if ('document_name' in doc) {
                return candidateName + ' - ' + doc.document_name;
            }
        }
        return t('noNameAvailable', { lng: language });
    };

    const getCurrentStatus = (document: AppDocument) => {
        const { status } = document;
        let currentStatus = '';
        let color = '';
        if (status === AppDocStatus.AUTO_FILLED) {
            color = StatusColor.green;
            currentStatus = 'Auto-Filled';
        } else if (status === AppDocStatus.SENT_TO_CLIENT) {
            color = StatusColor.blue;
            currentStatus = 'Sent to Client';
        } else if (status === AppDocStatus.CLIENT_COMPLETED) {
            color = StatusColor.orange;
            currentStatus = 'Client Completed';
        } else if (status === AppDocStatus.PRE_APPROVED) {
            color = StatusColor.green;
            currentStatus = 'Pre-Approved';
        } else if (status === AppDocStatus.MISSING) {
            color = StatusColor.red;
            currentStatus = `${t('missing', { lng: language })}`;
        } else if (status === AppDocStatus.FIX_REQUIRED) {
            color = StatusColor.orange;
            currentStatus = `${t('fixRequired', { lng: language })}`;
        } else if (status === AppDocStatus.WAITING_FOR_APPROVAL) {
            color = StatusColor.blue;
            currentStatus = `${t('waiting', { lng: language })}`;
        } else if (status === AppDocStatus.SIGNATURE_REQUIRED) {
            color = StatusColor.orange;
            currentStatus = `${t('signatureRequired', { lng: language })}`;
        } else if (status === AppDocStatus.PREAPPROVED) {
            color = StatusColor.green;
            currentStatus = `${t('preapproved', { lng: language })}`;
        } else if (status === AppDocStatus.APPROVED) {
            color = StatusColor.green;
            currentStatus = `${t('approved', { lng: language })}`;
        } else {
            color = StatusColor.orange;
            currentStatus = status;
        }

        return (
            <S.StatusContainer
                style={{ justifyContent: 'flex-start', width: '100%', marginLeft: 0 }}
            >
                <S.StatusDot color={color} />
                <S.StatusText>{currentStatus}</S.StatusText>
                <SelectDocStatusDropdown
                    mobile={mobile}
                    document={document}
                    handleChangeStatus={handleChangeStatus}
                    actionsPopover={false}
                />
            </S.StatusContainer>
        );
    };

    const handleOpenCloseDeleteDocModal = () => {
        setDeleteDocConfirmationModalOpenClose((state) => !state);
    };

    const openCloseDocViewerModal = () => {
        setDocViewerOpen((state) => !state);
    };

    const handleDisplayPDFViewer = async (doc: AppDocument) => {
        setDocForPDFViewer(doc);
        openCloseDocViewerModal();
    };

    const handleDeleteDoc = async (document: AppDocument[]) => {
        try {
            await dispatch(deleteDocs(document));
            if (activeApp) {
                dispatch(loadDocs(activeApp.id.toString()));
            }
        } catch (e) {
            console.error(e);
        }
    };

    const handleDownloadDoc = async (doc: AppDocument | Doc) => {
        if (activeApp?.id) {
            try {
                const resp = await api.documentResource.getBlobDocument(activeApp?.id, doc.id);
                const blob = resp.data;
                const fileURL = URL.createObjectURL(blob);
                const fileName =
                    resp.headers['resource_name'] ||
                    `${displayName(doc, activeApp?.candidate_name ?? '')}.pdf`;
                const anchor = document.createElement('a');
                anchor.href = fileURL;
                anchor.download = fileName;
                anchor.style.display = 'none';
                document.body.appendChild(anchor);
                anchor.click();
                anchor.remove();
                URL.revokeObjectURL(fileURL);
            } catch (err) {
                console.error('Failed to download:', err);
            }
        }
    };

    const handleUploadDoc = async (file: File, doc: AppDocument | Doc) => {
        const formData: FormData = new FormData();
        formData.append('document', file);
        try {
            await api.documentResource.post(doc.id, formData);
            if (activeApp) {
                dispatch(loadDocs(activeApp.id.toString()));
            }
        } catch {
            setAlertMessage({ type: 'error', value: t('somethingWentWrong', { lng: language }) });
            setShowingAlert(true);
        }
    };

    const handleUpdateDoc = async (
        document: AppDocument,
        title: string,
        explanation: string | null
    ) => {
        const payload = {
            appID: document.application_id.toString(),
            docID: document.id.toString(),
            payload: { document_name: title, explanation: explanation },
        };

        try {
            const res = await dispatch(patchDocProperty(payload));
            const updatedDocument = (res.payload as { data: AppDocument }).data;
            dispatch(updateDocInList(updatedDocument));
        } catch (e) {
            console.error(e);
        }
    };

    const handleReuploadDoc = async (file: File, doc: AppDocument | Doc) => {
        await handleUploadDoc(file, doc);
        setDocForPDFViewer({ ...doc });
    };

    const handleChangeStatus = async (document: AppDocument, newDocStatus: DocStatus) => {
        const payload = {
            appId: document.application_id.toString(),
            docId: document.id.toString(),
            payload: { document_name: document.document_name, status: newDocStatus },
        };
        try {
            const res = await dispatch(updateDoc(payload));
            const updatedDocument = (res.payload as { data: AppDocument }).data;
            dispatch(updateDocInList(updatedDocument));
        } catch (e) {
            console.error(e);
        }
    };

    const handleTableSelection = (selection: TableSelection) => {
        setDocumentTableSelection(selection);
    };

    const handlePreviewDocumentFile = async (
        document: ClientDocument,
        previewType: 'sample' | 'template'
    ) => {
        try {
            const response =
                previewType === 'template'
                    ? await api.application.getDocumentTemplateUrl(
                          String(document.application_id),
                          document.id
                      )
                    : await api.application.getDocumentSampleUrl(
                          String(document.application_id),
                          document.id
                      );

            setPreviewFile({
                fileUrl: response.data.url,
                fileName: `${document.document_name} ${
                    previewType === 'template' ? 'Template' : 'Sample'
                }`,
                title: `${previewType === 'template' ? 'Template' : 'Sample'} preview`,
            });
        } catch {
            setAlertMessage({ type: 'error', value: t('somethingWentWrong', { lng: language }) });
            setShowingAlert(true);
        }
    };

    const renderDescriptionIcon = (document: AppDocument) => (
        <S.InfoIconButton
            title={t('description', { lng: language })}
            onClick={() => setDescriptionDocument(document)}
        >
            <S.InfoIcon open={descriptionDocument?.id === document.id} />
        </S.InfoIconButton>
    );

    const docsGridStyle: React.CSSProperties = {
        display: 'grid',
        gridTemplateColumns: 'minmax(260px, 1fr) 220px 92px 120px 120px 178px',
        alignItems: 'center',
        columnGap: '0px',
    };

    return (
        <>
            <DocumentTableSelector
                selection={documentTableSelection}
                handleSelection={handleTableSelection}
                isPostItaDocs={postItaDocs.length > 0}
                isPostSubmissionDocs={postSubmissionDocs.length > 0}
            />
            <S.ListTable style={{ maxHeight: 'none', marginBottom: 16 }}>
                <LTHeader innerStyle={mobile ? undefined : docsGridStyle}>
                    {/* tooltips are present in LTItem itself */}
                    {/* <LTItem maxWidth="12%" minWidth="none" style={{ textAlign: 'center' }}>
                        Progress
                    </LTItem>

                    <LTItem maxWidth="13%" minWidth="13%">
                        Category
                    </LTItem> */}

                    <LTItem style={{ width: '100%' }}>{t('document', { lng: language })}</LTItem>

                    <LTItem style={{ width: '100%', textAlign: 'left' }}>
                        {t('status', { lng: language })}
                    </LTItem>
                    <LTItem
                        style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                        }}
                    >
                        {t('daysInStatus', { lng: language })}
                    </LTItem>

                    <LTItem style={{ width: '100%', textAlign: 'center' }}>Sample</LTItem>

                    <LTItem style={{ width: '100%', textAlign: 'center' }}>
                        {t('template', { lng: language })}
                    </LTItem>

                    <LTItem style={{ width: '100%', textAlign: 'center' }}>
                        {t('actions', { lng: language })}
                    </LTItem>
                </LTHeader>
                {!isDocsLoading && docsList.length === 0 && (
                    <>
                        <LTPlaceholder>{t('theListIsEmpty', { lng: language })}</LTPlaceholder>
                    </>
                )}
                {!isDocsLoading && docsList.length > 0 && (
                    <>
                        {docsForTable.map((document, i) => (
                            <S.GreyOnHover
                                key={document.id}
                                id={String(document.id)}
                                style={i % 2 === 0 ? {} : { background: Colors.darkTableRow }}
                            >
                                <CustomLTRow wrapperStyle={mobile ? undefined : docsGridStyle}>
                                    {/*FULL SCREEN VERSION*/}
                                    {/* <LTItem
                                    maxWidth="12%"
                                    minWidth="none"
                                    style={{ textAlign: 'center' }}>
                                    XX%
                                </LTItem>

                                <LTItem maxWidth="13%" minWidth="13%">
                                    needData
                                </LTItem> */}
                                    {document.resource_name ? (
                                        <LTItem
                                            tip={document.document_name}
                                            style={{ width: '100%' }}
                                        >
                                            <S.InfoIconRow mobile={false}>
                                                <S.FakeLink
                                                    onClick={() => handleDisplayPDFViewer(document)}
                                                >
                                                    {document.document_name}
                                                </S.FakeLink>
                                                {renderDescriptionIcon(document)}
                                            </S.InfoIconRow>
                                        </LTItem>
                                    ) : (
                                        <LTItem style={{ width: '100%' }}>
                                            <S.InfoIconRow mobile={false}>
                                                {document.document_name}
                                                {renderDescriptionIcon(document)}
                                            </S.InfoIconRow>
                                        </LTItem>
                                    )}

                                    <LTItem style={{ width: '100%', textAlign: 'left' }}>
                                        {getCurrentStatus(document)}
                                    </LTItem>
                                    <LTItem
                                        style={{
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-start',
                                        }}
                                    >
                                        {document.last_status_updated_at ? (
                                            (() => {
                                                const days = getDaysSince(
                                                    document.last_status_updated_at.toString()
                                                );
                                                return (
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '6px',
                                                        }}
                                                    >
                                                        {days}
                                                        {days > 7 && <ExclMark />}
                                                    </div>
                                                );
                                            })()
                                        ) : (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                }}
                                            >
                                                --
                                            </div>
                                        )}
                                    </LTItem>
                                    <LTItem style={{ width: '100%', textAlign: 'center' }}>
                                        {document.sample_uri ? (
                                            <S.Button
                                                onClick={() =>
                                                    handlePreviewDocumentFile(document, 'sample')
                                                }
                                                style={{ margin: '0 auto' }}
                                                title="Preview"
                                            >
                                                <Visibility fontSize="small" />
                                            </S.Button>
                                        ) : (
                                            '---'
                                        )}
                                    </LTItem>
                                    <LTItem style={{ width: '100%', textAlign: 'center' }}>
                                        {document.template_uri ? (
                                            <S.Button
                                                onClick={() =>
                                                    handlePreviewDocumentFile(document, 'template')
                                                }
                                                style={{ margin: '0 auto' }}
                                                title="Preview"
                                            >
                                                <Visibility fontSize="small" />
                                            </S.Button>
                                        ) : (
                                            '---'
                                        )}
                                    </LTItem>
                                    <LTItem style={{ width: '100%' }}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '10px',
                                            }}
                                        >
                                            <IconBtn
                                                title={t('deleteDocument', { lng: language })}
                                                style={{ padding: '0px' }}
                                            >
                                                <SvgIcon
                                                    style={{
                                                        width: '15',
                                                        height: '18px',
                                                        cursor: 'pointer',
                                                        fontSize: 'unset',
                                                    }}
                                                    viewBox="3 5 18 16.7"
                                                    onClick={() => {
                                                        setDocumentToDelete(document);
                                                        handleOpenCloseDeleteDocModal();
                                                    }}
                                                    component={BiTrash}
                                                />
                                            </IconBtn>
                                            <S.Button
                                                title={t('downloadDocument', { lng: language })}
                                                onClick={() => handleDownloadDoc(document)}
                                                disabled={document?.resource_name?.length === 0}
                                            >
                                                <DownloadIcon fill="#282828" />
                                            </S.Button>
                                            <FilePicker
                                                accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx"
                                                getFile={(file) => handleUploadDoc(file, document)}
                                            >
                                                <IconBtn
                                                    title={t('uploadDocument', {
                                                        lng: language,
                                                    })}
                                                    style={{ padding: '0px' }}
                                                >
                                                    <UploadIcon
                                                        fill="#282828"
                                                        style={{
                                                            transform: 'scale(1.1)',
                                                        }}
                                                    />
                                                </IconBtn>
                                            </FilePicker>
                                            <IconBtn
                                                title={t('createTicket', { lng: language })}
                                                onClick={() => setTicketDocument(document)}
                                                style={{ padding: '0px' }}
                                            >
                                                <BellIcon
                                                    fill="#282828"
                                                    style={{ width: '18px', height: '18px' }}
                                                />
                                            </IconBtn>
                                            <DocumentActionsPopover
                                                document={document}
                                                handleUpdateDoc={handleUpdateDoc}
                                                handleChangeStatus={handleChangeStatus}
                                                mobile={mobile}
                                            />
                                        </div>
                                    </LTItem>
                                    {/*SMALL SCREEN VERSION*/}
                                    {/* <LTMobileItem name="Progress">needData</LTMobileItem>
                                <LTMobileItem name="Category">needData</LTMobileItem> */}
                                    {document.resource_name ? (
                                        <LTMobileItem name={t('document', { lng: language })}>
                                            <S.InfoIconRow mobile={true}>
                                                <S.FakeLink
                                                    onClick={() => handleDisplayPDFViewer(document)}
                                                >
                                                    {document.document_name}
                                                </S.FakeLink>
                                                {renderDescriptionIcon(document)}
                                            </S.InfoIconRow>
                                        </LTMobileItem>
                                    ) : (
                                        <LTMobileItem name={t('document', { lng: language })}>
                                            <S.InfoIconRow mobile={true}>
                                                {document.document_name}
                                                {renderDescriptionIcon(document)}
                                            </S.InfoIconRow>
                                        </LTMobileItem>
                                    )}

                                    <LTMobileItem name={t('status', { lng: language })}>
                                        {getCurrentStatus(document)}
                                    </LTMobileItem>

                                    <LTMobileItem name={t('daysInStatus', { lng: language })}>
                                        {document.last_status_updated_at ? (
                                            (() => {
                                                const days = getDaysSince(
                                                    document.last_status_updated_at.toString()
                                                );
                                                return (
                                                    <div>
                                                        {days}
                                                        {days > 7 && <ExclMark />}
                                                    </div>
                                                );
                                            })()
                                        ) : (
                                            <div>--</div>
                                        )}
                                    </LTMobileItem>

                                    <LTMobileItem name="Sample">
                                        {document.sample_uri ? (
                                            <S.Button
                                                onClick={() =>
                                                    handlePreviewDocumentFile(document, 'sample')
                                                }
                                                style={{ margin: '0 auto' }}
                                                title="Preview"
                                            >
                                                <Visibility fontSize="small" />
                                            </S.Button>
                                        ) : (
                                            '---'
                                        )}
                                    </LTMobileItem>

                                    <LTMobileItem name={t('template', { lng: language })}>
                                        {document.template_uri ? (
                                            <S.Button
                                                onClick={() =>
                                                    handlePreviewDocumentFile(document, 'template')
                                                }
                                                style={{ margin: '0 auto' }}
                                                title="Preview"
                                            >
                                                <Visibility fontSize="small" />
                                            </S.Button>
                                        ) : (
                                            '---'
                                        )}
                                    </LTMobileItem>

                                    <LTMobileItem name={t('actions', { lng: language })}>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <IconBtn
                                                style={{
                                                    padding: '0px',
                                                    marginRight: '3px',
                                                    marginTop: '5px',
                                                }}
                                            >
                                                <SvgIcon
                                                    style={{
                                                        width: '15',
                                                        height: '18.7px',
                                                        cursor: 'pointer',
                                                        fontSize: 'unset',
                                                    }}
                                                    viewBox="3 5 18 16.7"
                                                    onClick={() => {
                                                        setDocumentToDelete(document);
                                                        handleOpenCloseDeleteDocModal();
                                                    }}
                                                    component={BiTrash}
                                                />
                                            </IconBtn>

                                            <S.Button
                                                onClick={() => handleDownloadDoc(document)}
                                                disabled={document?.resource_name?.length === 0}
                                                style={{
                                                    marginTop: '1px',
                                                    marginRight: '3px',
                                                }}
                                            >
                                                <DownloadIcon fill="#282828" />
                                            </S.Button>

                                            <FilePicker
                                                accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx"
                                                getFile={(file) => handleUploadDoc(file, document)}
                                            >
                                                <IconBtn
                                                    style={{
                                                        padding: '0px',
                                                        marginRight: '3px',
                                                    }}
                                                >
                                                    <UploadIcon
                                                        fill="#282828"
                                                        style={{
                                                            width: '22px',
                                                            height: '17px',
                                                            marginBottom: '0.1rem',
                                                        }}
                                                    />
                                                </IconBtn>
                                            </FilePicker>

                                            <IconBtn
                                                title={t('createTicket', { lng: language })}
                                                onClick={() => setTicketDocument(document)}
                                                style={{
                                                    padding: '0px',
                                                    marginRight: '3px',
                                                }}
                                            >
                                                <BellIcon
                                                    fill="#282828"
                                                    style={{ width: '18px', height: '18px' }}
                                                />
                                            </IconBtn>

                                            <div style={{ marginRight: '1px' }}>
                                                <DocumentActionsPopover
                                                    document={document}
                                                    handleUpdateDoc={handleUpdateDoc}
                                                    handleChangeStatus={handleChangeStatus}
                                                    mobile={mobile}
                                                />
                                            </div>
                                        </div>
                                    </LTMobileItem>
                                </CustomLTRow>
                            </S.GreyOnHover>
                        ))}
                    </>
                )}
                {isDocsLoading &&
                    skeletonArray.map((_, idx) => (
                        <LTRow key={idx}>
                            {/* <LTItem maxWidth="10%">
                                <Skeleton width={78} />
                            </LTItem>
                            <LTItem minWidth="13%">
                                <Skeleton width={85} />
                            </LTItem> */}
                            <LTItem minWidth="25%">
                                <Skeleton width={100} />
                            </LTItem>
                            <LTItem minWidth="25%">
                                <Skeleton width={100} />
                            </LTItem>
                            <LTItem minWidth="25%">
                                <Skeleton width={100} />
                            </LTItem>
                            <LTItem minWidth="25%">
                                <Skeleton width={100} />
                            </LTItem>
                            <LTItem minWidth="25%">
                                <Skeleton width={100} />
                            </LTItem>
                            <LTItem minWidth="25%" lastItem>
                                <Skeleton height={36} width={150} />
                            </LTItem>
                        </LTRow>
                    ))}
            </S.ListTable>

            <AlertMessage
                type={alertMessage?.type}
                text={alertMessage?.value}
                open={isShowingAlert}
                onClose={() => setShowingAlert(false)}
            />
            <Modal
                sx={{ zIndex: '10000' }}
                open={isDocViewerOpen}
                onClose={openCloseDocViewerModal}
            >
                <S.ModalWrapper>
                    <S.Modal>
                        <S.ButtonWrapper>
                            <IconBtn
                                style={{ height: '24px', width: '24px' }}
                                onClick={openCloseDocViewerModal}
                            >
                                <CrossSimpleIcon style={{ fill: '#282828' }} />
                            </IconBtn>
                        </S.ButtonWrapper>
                        <DocumentPreview
                            doc={docForPDFViewer}
                            downloadDoc={handleDownloadDoc}
                            uploadDoc={handleReuploadDoc}
                        />
                    </S.Modal>
                </S.ModalWrapper>
            </Modal>
            <UrlDocumentPreview
                open={Boolean(previewFile)}
                mobile={mobile}
                onClose={() => setPreviewFile(null)}
                title={previewFile?.title}
                fileUrl={previewFile?.fileUrl}
                fileName={previewFile?.fileName}
            />

            <DeleteDocumentConfirmationModal
                toggleDeleteDocModal={handleOpenCloseDeleteDocModal}
                handleDeleteDoc={handleDeleteDoc}
                document={documentToDelete}
                open={deleteDocConfirmationModalOpen}
                mobile={mobile}
            />

            <CreateTicketModal
                open={ticketDocument !== null}
                onClose={() => setTicketDocument(null)}
                mobile={mobile}
                documentId={ticketDocument?.id}
                documentName={ticketDocument?.document_name}
            />

            <DocumentDescriptionModal
                document={descriptionDocument}
                onClose={() => setDescriptionDocument(null)}
            />
        </>
    );
}
